import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

const STATUS_LABELS = {
  pending_payment: { text: 'Ожидает оплаты', color: '#FFA000' },
  active: { text: 'Активен', color: '#2E7D32' },
  activated: { text: 'Активирован', color: '#1A73E8' },
  expired: { text: 'Истёк срок активации', color: '#9E9E9E' },
  cancelled: { text: 'Отозван', color: '#C62828' },
};

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return '';
  }
}

function CertificateCard({ cert, type, onCopy, onShare }) {
  const status = STATUS_LABELS[cert.status] || { text: cert.status, color: '#666' };
  const isPurchased = type === 'purchased';
  const canShare = isPurchased && cert.code && cert.status === 'active' && typeof navigator !== 'undefined' && !!navigator.share;

  return (
    <div style={{
      background: '#FFF',
      borderRadius: 12,
      padding: 14,
      border: '1px solid #EAEAEA',
      marginBottom: 10,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 18, fontWeight: 700 }}>
          {cert.amount.toLocaleString('ru-RU')} ₽
        </div>
        {type !== 'activated' && (
          <span style={{
            fontSize: 12,
            padding: '3px 8px',
            borderRadius: 12,
            background: status.color + '20',
            color: status.color,
            fontWeight: 500,
          }}>
            {status.text}
          </span>
        )}
      </div>

      {isPurchased && cert.code && cert.status !== 'activated' && cert.status !== 'pending_payment' && (
        <div style={{
          background: '#F5F5F5',
          borderRadius: 8,
          padding: '8px 10px',
          marginBottom: 8,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <code style={{ fontSize: 14, letterSpacing: 1, fontWeight: 600 }}>{cert.code}</code>
          <button
            type="button"
            onClick={() => onCopy?.(cert.code)}
            style={{ background: 'none', border: 'none', color: '#1A73E8', cursor: 'pointer', fontSize: 13 }}
          >
            Скопировать
          </button>
        </div>
      )}

      <div style={{ fontSize: 13, color: '#666', lineHeight: 1.6 }}>
        {type !== 'activated' && cert.recipient_name && <div>Для: <b>{cert.recipient_name}</b></div>}
        {type !== 'activated' && cert.sender_name && <div>От: <b>{cert.sender_name}</b></div>}
        {type !== 'activated' && cert.message && (
          <div style={{ marginTop: 4, fontStyle: 'italic' }}>«{cert.message}»</div>
        )}
        {cert.status === 'active' && cert.expires_at && (
          <div style={{ marginTop: 4 }}>Действует до {formatDate(cert.expires_at)}</div>
        )}
        {cert.status === 'activated' && cert.activated_at && (
          <div style={{ marginTop: 4 }}>Дата активации {formatDate(cert.activated_at)}</div>
        )}
        {type !== 'activated' && cert.created_at && (
          <div style={{ marginTop: 4, color: '#999' }}>Куплен {formatDate(cert.created_at)}</div>
        )}
      </div>

      {isPurchased && cert.image_url && cert.status === 'active' && (
        <div style={{ marginTop: 10, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <a
            href={cert.image_url}
            target="_blank"
            rel="noreferrer"
            style={{ fontSize: 13, color: '#1A73E8' }}
          >
            Открыть картинку
          </a>
          {canShare && (
            <button
              type="button"
              onClick={() => onShare?.(cert)}
              style={{
                fontSize: 13,
                background: 'none',
                border: '1px solid #1A73E8',
                color: '#1A73E8',
                borderRadius: 6,
                padding: '4px 10px',
                cursor: 'pointer',
              }}
            >
              Поделиться
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function CertificatesList({ refreshKey = 0 }) {
  const [data, setData] = useState({ purchased: [], activated: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(() => {
    setIsLoading(true);
    api.getMyCertificates()
      .then(setData)
      .catch(e => setError(e.message || 'Ошибка загрузки'))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (isLoading) return null;
  if (error) return <div style={{ padding: 12, color: '#C62828', fontSize: 13 }}>{error}</div>;

  // Покупки сертификатов теперь только в «Мои заказы». Здесь — только история активаций.
  if (data.activated.length === 0) return null;

  return (
    <div style={{ marginTop: 12 }}>
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '10px 12px',
          background: '#F5F5F5',
          border: 'none',
          borderRadius: 10,
          cursor: 'pointer',
          fontSize: 14,
          fontWeight: 500,
          color: '#444',
        }}
      >
        <span>История активаций ({data.activated.length})</span>
        <span style={{ fontSize: 12, color: '#999' }}>{expanded ? '▲' : '▼'}</span>
      </button>
      {expanded && (
        <div style={{ marginTop: 10 }}>
          {data.activated.map(c => (
            <CertificateCard key={c.id} cert={c} type="activated" />
          ))}
        </div>
      )}
    </div>
  );
}

export default CertificatesList;
